/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState, useContext } from "react";
import { PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";
import { translateDataValue } from "../../i18n/translateData";
import { useHistory, useLocation } from "react-router-dom";

import forIn from "lodash/forIn";

import { Row, Col, Form } from "react-bootstrap";
import Sallie from "../Sallie";
import ListSelector from "../ListSelector";
import useListSelector from "../../lib/useListSelector";
import OnboardingStepHeader from "./OnboardingStepHeader";
import SubmitButton from "../StyledComponents/SubmitButton";

import {
    postTrackedFactors,
    postWorstSymptomsFactors,
} from "../../api/TBIRequests";
import customizeMessages from "../../lib/customizeMessages";

const SelectTopSymptoms = ({ setOnboardingPercent, user }) => {
    const { patient } = useContext(PatientContext);
    const { t, language } = useTranslation();

    const history = useHistory();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [noSymptom, setNoSymptom] = useState(false);
    const { forwardData } = location.state || { forwardData: undefined };
    const symptomMapping = forwardData?.symptomMapping || {}; // Receive mapping from SelectLaterSymptoms

    const [returnedData, setReturnedData] = useState([]);

    const {
        toggleFromSelectedList,
        switchSingleSelection,
        selectedData,
        initialize,
    } = useListSelector({
        setInitialData: setReturnedData,
        initialData: returnedData,
    });

    if (!patient) {
        history.push("/oops");
    }

    const createItemList = () => {
        const items = [];
        
        // First, convert all symptoms to English (in case they're in Spanish)
        forIn(forwardData.selectedData, (value, category) => {
            value.forEach((item) => {
                // Convert to English first using reverse mapping
                let englishSymptom = item;
                if (symptomMapping[category] && symptomMapping[category][item]) {
                    englishSymptom = symptomMapping[category][item];
                }
                
                // Now translate to current language
                const translatedSymptom = translateDataValue(t, "onboarding.symptomListNew", category, englishSymptom);
                items.push(translatedSymptom);
            });
        });
        
        const itemList = [
            {
                category: "data",
                items,
                maxSelectAmount: "5",
            },
        ];
        return itemList;
    };

    const worstSymptomsForSending = () => {
        // Get selected symptoms (displayed in current language)
        const selectedSymptoms = Object.values(selectedData).flat();
        
        console.log("=== DEBUG: worstSymptomsForSending ===");
        console.log("Selected symptoms (as displayed):", selectedSymptoms);
        console.log("Symptom mapping:", symptomMapping);
        console.log("Processed data factors:", forwardData.processedData.map(item => item.factor));
        
        // Convert displayed symptoms back to English for backend
        const selectedSymptomsInEnglish = selectedSymptoms.map(symptom => {
            // First check if it's already in processedData (already English)
            const alreadyInEnglish = forwardData.processedData.find(item => item.factor === symptom);
            if (alreadyInEnglish) {
                return symptom;
            }
            
            // Try to find original English name in mapping
            for (const category in symptomMapping) {
                if (symptomMapping[category] && symptomMapping[category][symptom]) {
                    return symptomMapping[category][symptom];
                }
            }
            
            // If not found in mapping, return as-is
            return symptom;
        });
        
        console.log("Selected symptoms (converted to English):", selectedSymptomsInEnglish);
        
        const filtered = forwardData.processedData
            .filter((item) => selectedSymptomsInEnglish.includes(item.factor))
            .map((item) => ({
                factor: item.factor,
                category: item.category,
                subcategory: item.subcategory,
            }));
        
        console.log("Filtered results:", filtered);
        console.log("=== END DEBUG ===");
        
        return filtered;
    };

    const allTrackingSymptomsForSending = () => {
        return (
            forwardData.processedData
                // .filter((item) =>
                //   Object.values(selectedData).flat().includes(item.factor)
                // )
                .map((item) => ({
                    factor: item.factor,
                    category: item.category,
                    subcategory: item.subcategory,
                }))
        );
    };

    useEffect(() => {
        if (!forwardData) {
            // api call, right now user must do onboarding in one full swoop
        } else {
            const itemList = createItemList();
            initialize(itemList);
            setReturnedData(itemList);
        }
        setLoading(false);
        location.state?.fromPatientControl
            ? setOnboardingPercent(80)
            : setOnboardingPercent(50);
    }, [forwardData, language]); // Add language as dependency

    const handleSubmit = () => {
        const worstSymptoms = worstSymptomsForSending();
        const allTrackingSymptoms = allTrackingSymptomsForSending();
        
        // Check if exactly 5 symptoms are selected
        if (worstSymptoms.length !== 5) {
            setNoSymptom(true);
            return;
        }
        
        if (allTrackingSymptoms.length === 0) {
            setNoSymptom(true);
            return;
        }

        setSubmitting(true);
        setNoSymptom(false);

        postWorstSymptomsFactors({
            patientId: patient.patientId,
            data: worstSymptoms,
        }).catch((error) => {
            // Handle errors
            console.error(
                "An error occurred while posting tracked factors:",
                error
            );
            history.push("/oops");
        });

        postTrackedFactors({
            patientId: patient.patientId,
            data: allTrackingSymptoms,
        })
            .then(() => {
                location.state?.fromPatientDashboard
                    ? //add more symptoms,if there's no tracking factors
                      history.push("/", { fromPatientDashboard: true })
                    : history.push("/selectTrigger", {
                          fromPatientControl:
                              location.state?.fromPatientControl,
                      });
            })
            .catch((error) => {
                // Handle errors
                console.error(
                    "An error occurred while posting tracked factors:",
                    error
                );
                history.push("/oops");
            });
    };

    const getPossibleMessages = () => ({
        mainHeader: {
            Stroke: t("onboarding.topSymptoms.header"),
            tbiPatient: t("onboarding.topSymptoms.header"),
            caregiver: t("onboarding.topSymptoms.header"),
        },
        sallieText: {
            Stroke: t("onboarding.sallieTopSymptoms"),
            tbiPatient: t("onboarding.sallieTopSymptoms"),
            caregiver: t("onboarding.sallieTopSymptoms"),
        },
        // messageAfterList: {
        //   Stroke: "You can change your symptoms, or you can add more later.",
        //   tbiPatient: "You can change your symptoms, or you can add more later.",
        //   caregiver:
        //     "You can change the patient's symptoms, or you can add more later.",
        // },
        noSymptomsText: {
            Stroke: t("onboarding.topSymptoms.error"),
            tbiPatient: t("onboarding.topSymptoms.error"),
            caregiver: t("onboarding.topSymptoms.error"),
        },
        selectSymptomsCategory: {
            Stroke: t("onboarding.topSymptoms.body"),
            tbiPatient: t("onboarding.topSymptoms.body"),
            caregiver: t("onboarding.topSymptoms.body"),
        },
    });

    const getMessage = customizeMessages({ user, getPossibleMessages });

    return (
        <>
            {loading ? (
                t("common.loading")
            ) : (
                <>
                    <OnboardingStepHeader text={getMessage("mainHeader")} />{" "}
                    <Sallie text={getMessage("sallieText")} />
                    {returnedData.map((itemList, i) => (
                        <ListSelector
                            key={itemList.category}
                            category={itemList.category}
                            message={getMessage("selectSymptomsCategory")}
                            list={itemList.items}
                            index={i}
                            selectedList={selectedData[itemList.category]}
                            toggleFromSelectedList={toggleFromSelectedList}
                            selectOne={switchSingleSelection}
                            maxSelectAmount={itemList.maxSelectAmount}
                            noOther
                            noSuffix
                        />
                    ))}
                    {/* {getMessage("messageAfterList")} */}
                    <Row>
                        <Col>
                            {noSymptom && (
                                <Form.Control.Feedback
                                    style={{ display: "block" }}
                                    type="invalid"
                                >
                                    {getMessage("noSymptomsText")}
                                </Form.Control.Feedback>
                            )}
                        </Col>
                        <Col>
                            {location.state?.fromPatientDashboard ? (
                                <SubmitButton
                                    disabled={submitting}
                                    type="button"
                                    onClick={handleSubmit}
                                >
                                    {t("onboarding.saveAndGoToDashboard")} &gt;
                                </SubmitButton>
                            ) : (
                                <SubmitButton
                                    disabled={submitting}
                                    type="button"
                                    onClick={handleSubmit}
                                >
                                    {t("onboarding.saveContinue")} &gt;
                                </SubmitButton>
                            )}
                        </Col>
                    </Row>
                </>
            )}
        </>
    );
};

export default SelectTopSymptoms;